import React, {useEffect} from 'react';
import {Form, Formik, FormikHelpers} from "formik";
import {TextField} from "components/Form/Form";
import {Conversation, ConversationData, ConversationMessage} from "../../../types/Conversation";
import {UserMessage} from "./UserMessage";
import {AssistantMessage} from "./AssistantMessage";
import Spinner from "../../../components/Spinner";
import gageIcon from "assets/gage.svg";
import {ArrowUpIcon, MicrophoneIcon, QuestionMarkCircleIcon, XIcon} from "@heroicons/react/outline";
import {concatenate} from "../../../services/string";
import {useSpeechRecognition} from "../../../hooks/useSpeechRecognition";
import {Bubble} from "../../../components/Bubble";
import {Transition} from "@headlessui/react";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import CircularButton from "../../../components/CircularButton";
import {hasSpeechRecognitionSupport} from "../../../services/browser-support";

// Types ----

type ConversationFormProps = {
  conversation?: Conversation | null
  onSubmit: (values: ConversationData, helpers: FormikHelpers<ConversationData>) => any
  isLoading?: boolean
  question?: string
}

// Component ----

function ConversationForm({conversation, onSubmit, isLoading, question}: ConversationFormProps) {
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const formikRef = React.useRef<any>(null);
  const {stopSpeechRecognition, startSpeechRecognition, listeningForSpeech, speechText} = useSpeechRecognition();

  const isSpeechSupported = hasSpeechRecognitionSupport();

  const initialValues: ConversationData = {
    question: '',
  }

  // Scroll to bottom when new message is added
  useEffect(() => {
      bottomRef.current?.scrollIntoView({behavior: "smooth"});
  }, [conversation?.messages.length]);

  // Update question form field when we have speech text
  useEffect(() => {
    if (!formikRef.current || !speechText) return;

    formikRef.current.setFieldValue('question', speechText);
  }, [speechText]);

  return (
    <div className="flex flex-col shadow sm:rounded-md px-4 pt-8 pb-24 bg-white min-h-[calc(100dvh-200px)]">
      {(!conversation) ? (
          <div className='flex flex-1 items-center justify-center'>

              <div className='flex flex-col gap-3 items-center'>
                <Transition
                  appear
                  enter="transition-all duration-500 delay-300"
                  enterFrom="opacity-0 translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                >
                  <img src={gageIcon} className='w-[100px]' alt='Gage AI robot.'/>
                </Transition>

                <Transition
                  appear
                  enter="transition-all duration-700 delay-700"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                >
                  <p className='text-md text-gray-500 '>
                    {isLoading ? 'I\'ll be back with an answer in a jiffy!' : 'Hello, I\'m Gage! Have a question for me?'}
                  </p>
                </Transition>

                <Transition
                  show={!!question}
                  enter="transition-all duration-300"
                  enterFrom="opacity-0 translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                >
                  {question &&
                    <div className='w-full max-w-[60ch] mt-4 text-sm'>
                      <Bubble
                        label='You asked...'
                        icon={QuestionMarkCircleIcon}
                        text={question}
                      />
                    </div>
                  }
                </Transition>
              </div>
          </div>
        ) : (
          <div className='flex flex-col gap-6 w-full max-w-[76ch] mx-auto text-sm text-gray-900 line leading-normal'>
            {/* @ts-ignore*/}
            {conversation?.messages?.map((message: ConversationMessage) => {
              switch(message?.role) {
                case 'user':
                  return (
                    <UserMessage message={message} key={message.id} />
                  )
                case 'assistant':
                  return (
                    <AssistantMessage message={message} key={message.id} />
                  )
                default:
                  return null;
              }
            })}
            <div ref={bottomRef}></div>
          </div>
        )
      }

      <div className='flex flex-col justify-center items-center fixed bottom-0 left-0 md:left-[16rem] right-0 bg-white px-4 sm:px-6 lg:px-8 h-[73px] border-t border-gray-200'>
        <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={onSubmit}>
          {formik => {
            function handleSpeechRecognition() {
              if (listeningForSpeech) {
                stopSpeechRecognition();
              } else {
                void formik.setFieldValue('question', '');
                startSpeechRecognition();
              }
            }

            return (
              <Form className="flex gap-3 items-center w-full max-w-[96rem]">
                <ErrorNotification formik={formik} />

                {/* Message input field */}
                <div className='flex-1'>
                  <TextField
                    name='question'
                    placeholder='e.g. How many visitors have we had this year?'
                  />
                </div>

                {/* Form buttons */}
                <div className='flex gap-2 justify-center items-center shrink-0'>
                  {isSpeechSupported &&
                    <CircularButton
                      icon={listeningForSpeech ? XIcon : MicrophoneIcon}
                      size={CircularButton.sizes.SMALL}
                      color={CircularButton.colors.GREY}
                      onClick={handleSpeechRecognition}
                      disabled={isLoading}
                    />
                  }
                  <CircularButton
                    icon={ArrowUpIcon}
                    size={CircularButton.sizes.SMALL}
                    color={CircularButton.colors.SECONDARY}
                    onClick={() => formik.submitForm()}
                    disabled={isLoading || !formik.values.question}
                  />
                </div>

                {/* Loading indicator */}
                <div className={
                  concatenate(
                    'absolute left-1/2 top-[-50px] translate-x-[-50%] flex gap-3 items-center bg-[#d956fc] rounded-full px-3 py-1.5 shadow shadow-gray-300 transition-all duration-200 pointer-events-none',
                    isLoading ? 'translate-y-[0] opacity-100 visible' : 'translate-y-[50%] opacity-0 invisible'
                  )}
                >
                  <Spinner color={Spinner.colors.LIGHT} size={Spinner.sizes.X_SMALL}/>
                  <div className='text-white text-sm'>Thinking...</div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  );
}

// Export ----

export default ConversationForm;
